import { Check, Copy, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog } from "../../ui";

/**
 * Shows a freshly issued app key.
 *
 * The server hashes the key and keeps only the hash, so this is genuinely the
 * one and only time it can be read. The dialog says so plainly and makes
 * copying a single click, because the alternative to reading it now is issuing
 * a new one.
 */
export function SecretOnceDialog({
  secret,
  username,
  onClose,
}: {
  secret: string | null;
  username: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is blocked outside a secure context or without permission —
      // the key is on screen and selectable, so this is not fatal.
    }
  };

  return (
    <Dialog
      open={secret !== null}
      onClose={onClose}
      title={t("app-account.key.title", "API key for {{username}}", { username })}
      size="md"
      actions={
        <Button variant="primary" onClick={onClose}>
          {t("app-account.key.done", "I've saved it")}
        </Button>
      }
    >
      <div className="flex items-start gap-2.5 rounded-md bg-warn-soft px-3 py-2.5 text-[13px] text-warn">
        <TriangleAlert className="mt-0.5 size-4 shrink-0" />
        <p>
          {t(
            "app-account.key.warning",
            "Copy this key now. Only its hash is stored, so it cannot be shown again. If it is lost, rotate the key to get a new one.",
          )}
        </p>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <code className="min-w-0 flex-1 select-all break-all rounded-md border border-line bg-surface-sunk px-3 py-2 text-[13px] text-ink">
          {secret}
        </code>
        <Button
          variant="secondary"
          onClick={() => void copy()}
          icon={copied ? <Check className="size-4 text-ok" /> : <Copy className="size-4" />}
        >
          {copied ? t("app-account.key.copied", "Copied") : t("app-account.key.copy", "Copy")}
        </Button>
      </div>
    </Dialog>
  );
}
